/**
 * The warmup routine DRAFT — pure logic, no React, no Firestore, no audio.
 *
 * Anthony, 2026-09-03: "when a teacher wants to swap an exercise there should
 * be an option to save the updated routine or discard before they are done",
 * and the reached top "will it save the 'up to' note for that student?" →
 * yes, as part of the same draft. So a lesson holds two routines: SAVED (the
 * student's document, or nothing on a first lesson) and DRAFT (what the
 * teacher is looking at). This module answers the questions the UI asks about
 * the pair — is it dirty, what changed in words, how does a reached top fold
 * in — and serialises the draft for the per-lesson sessionStorage keep that
 * makes leaving a lesson non-destructive (same shape as useLessonScratch).
 *
 * THE OCTAVE RULE (Anthony: "I agree about the octave"): a drop-octave-then-
 * climb never counts as a new top. Callers compare the UN-SHIFTED key — the
 * player subtracts `octaveShift` before reporting `topReached`.
 */
import { midiToName, nameToMidi, topTonicFor } from './warmupStepPlan';
import { assembleRoutine, ageBandFor, DURATION_WORDS, VOICE_WORDS } from './warmupRoutineModel';
import matrix from '../data/warmupMatrix.json';

export const draftKey = (lessonId) => `cyv.warmupDraft.${lessonId}`;

const LEVEL_WORDS = { beg: 'Beginner', int: 'Intermediate', adv: 'Advanced' };
const CELL_LABEL = { duration: 'Length', level: 'Level', ageBand: 'Ages', style: 'Style', voice: 'Voice' };
const cellWord = (key, value) => {
	if (key === 'duration') return (DURATION_WORDS[value] || value).split(' · ')[0];
	if (key === 'level') return LEVEL_WORDS[value] || value;
	if (key === 'voice') return (VOICE_WORDS[value] || value).split(' (')[0];
	return value;
};

// ---------------------------------------------------------------------------
// sessionStorage keep — guarded like useLessonScratch: a private window throws
// on read, and losing a draft must never take the lesson down.
// ---------------------------------------------------------------------------

export const readDraft = (lessonId) => {
	if (!lessonId) return null;
	try {
		const raw = sessionStorage.getItem(draftKey(lessonId));
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		return parsed && typeof parsed === 'object' && Array.isArray(parsed.items) ? parsed : null;
	} catch {
		return null;
	}
};

export const writeDraft = (lessonId, draft) => {
	if (!lessonId) return;
	try {
		if (!draft) sessionStorage.removeItem(draftKey(lessonId));
		else sessionStorage.setItem(draftKey(lessonId), JSON.stringify(draft));
	} catch {
		/* best-effort */
	}
};

export const clearDraft = (lessonId) => writeDraft(lessonId, null);

// ---------------------------------------------------------------------------
// Comparing SAVED and DRAFT
// ---------------------------------------------------------------------------

/** The fields that make two routines "the same routine" — nothing derived. */
const itemSig = (it) => `${it.slot}|${it.exerciseId}|${it.startTonic}|${it.topTonic}|${it.bpm}`;
const cellSig = (cell) => ['duration', 'level', 'ageBand', 'style', 'voice'].map((k) => cell?.[k] ?? '').join('|');

export const sameRoutine = (a, b) => {
	if (!a || !b) return a === b;
	if (cellSig(a.cell) !== cellSig(b.cell)) return false;
	if ((a.items || []).length !== (b.items || []).length) return false;
	return a.items.every((it, i) => itemSig(it) === itemSig(b.items[i]));
};

export const isDirty = (saved, draft) => Boolean(draft) && !sameRoutine(saved, draft);

/**
 * Human lines for the "Unsaved changes" header. Order: settings, swaps,
 * skips, tops — the order a teacher would list them.
 *
 * Matching is by SLOT when the sequences line up, otherwise by exercise id,
 * so a skip does not read as eight swaps. `byId` resolves names.
 */
export const describeChanges = (saved, draft, byId = new Map()) => {
	if (!draft) return [];
	const name = (id) => byId.get(id)?.name || id;
	const lines = [];
	if (!saved) {
		lines.push(`New routine for this student — ${draft.items.length} exercises`);
		return lines;
	}
	for (const key of ['duration', 'level', 'ageBand', 'style', 'voice']) {
		if ((saved.cell?.[key] ?? '') !== (draft.cell?.[key] ?? '')) {
			lines.push(`${CELL_LABEL[key]} ${cellWord(key, saved.cell?.[key])} → ${cellWord(key, draft.cell?.[key])}`);
		}
	}
	const savedIds = new Set(saved.items.map((it) => it.exerciseId));
	const draftIds = new Set(draft.items.map((it) => it.exerciseId));
	// Swaps: same slot index, different exercise, and the old one is gone.
	const swapped = new Set();
	if (saved.items.length === draft.items.length) {
		draft.items.forEach((it, i) => {
			const was = saved.items[i];
			if (was && was.slot === it.slot && was.exerciseId !== it.exerciseId && !draftIds.has(was.exerciseId)) {
				lines.push(`Swapped ${name(was.exerciseId)} → ${name(it.exerciseId)}`);
				swapped.add(was.exerciseId);
				swapped.add(it.exerciseId);
			}
		});
	}
	saved.items.forEach((it) => {
		if (!draftIds.has(it.exerciseId) && !swapped.has(it.exerciseId)) lines.push(`Skipped ${name(it.exerciseId)}`);
	});
	draft.items.forEach((it) => {
		if (!savedIds.has(it.exerciseId) && !swapped.has(it.exerciseId)) lines.push(`Added ${name(it.exerciseId)}`);
	});
	draft.items.forEach((it) => {
		const was = saved.items.find((s) => s.exerciseId === it.exerciseId);
		if (was && was.topTonic !== it.topTonic && was.startTonic === it.startTonic) {
			lines.push(`${name(it.exerciseId)} up to ${midiToName(it.topTonic)}, was ${midiToName(was.topTonic)}`);
		}
	});
	return lines;
};

// ---------------------------------------------------------------------------
// Folding a reached top into the draft
// ---------------------------------------------------------------------------

/**
 * A student reached `topReached` (UN-shifted MIDI) on item `index`. Returns a
 * new routine with that item's top and span updated, or the same routine when
 * nothing changed. Both directions: a student who went higher than planned
 * AND one who was brought down early (Anthony: "same mechanism, both
 * directions"). Flags `aboveBand` when the top passes the age band's clamp —
 * shown, never blocked: "saving is still your call".
 */
export const applyReachedTop = (routine, index, topReached, byId = new Map()) => {
	const item = routine?.items?.[index];
	if (!item || !Number.isFinite(topReached)) return routine;
	const top = Math.max(item.startTonic, Math.round(topReached));
	if (top === item.topTonic) return routine;
	const ex = byId.get(item.exerciseId);
	const bandTop = ex && routine.cell?.ageBand ? topTonicFor(ex, item.startTonic, routine.cell.ageBand, matrix) : null;
	const items = routine.items.map((it, i) =>
		i === index ? { ...it, topTonic: top, span: top - it.startTonic, aboveBand: bandTop != null && top > bandTop } : it
	);
	return { ...routine, items };
};

// ---------------------------------------------------------------------------
// A first routine for a student
// ---------------------------------------------------------------------------

/** Age from a stored birthday string ("2014-03-09" or "03/09/2014"), or null. */
export const ageFromBirthday = (birthday, now = new Date()) => {
	if (!birthday) return null;
	const s = String(birthday).trim();
	let y;
	let m;
	let d;
	let match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
	if (match) [, y, m, d] = match.map(Number);
	else {
		match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
		if (!match) return null;
		[, m, d, y] = match.map(Number);
	}
	let age = now.getFullYear() - y;
	if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)) age -= 1;
	return age >= 0 && age < 120 ? age : null;
};

/**
 * The cell to propose for a student with no saved routine: age band from the
 * student doc (`age`, else `birthday`), the matrix's own guidance for voice
 * ("high: most 8-13, sopranos"), and the studio defaults for the rest. A
 * 45-minute lesson gets the 10-minute routine.
 */
export const proposeCell = (student, now = new Date()) => {
	const age = Number.isFinite(Number(student?.age)) && student?.age !== '' && student?.age != null ? Number(student.age) : ageFromBirthday(student?.birthday, now);
	const ageBand = ageBandFor(age) || '11-13';
	return { duration: 'mid', level: 'beg', ageBand, style: 'legit', voice: ageBand === '14-18' ? 'mid' : 'high' };
};

/** Assemble the first routine for a student. */
export const newRoutineForStudent = (student, catalog, now = new Date()) => {
	const cell = proposeCell(student, now);
	const r = assembleRoutine(cell, catalog);
	return { cell, items: r.items, seconds: r.seconds, band: r.band, inBand: r.inBand, error: r.error };
};

/** A saved document → the in-memory routine shape the player uses. */
export const routineFromDoc = (docData) => {
	if (!docData || !Array.isArray(docData.items)) return null;
	const cell = docData.cell || {};
	return { cell, items: docData.items, band: matrix.bands[cell.duration] || null, seconds: docData.items.reduce((a, it) => a + (it.seconds || 0), 0), inBand: true, error: null };
};

// ---------------------------------------------------------------------------
// The last lesson's log, in one line
// ---------------------------------------------------------------------------

const shortDate = (iso) => {
	if (!iso) return '';
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
	if (!m) return String(iso);
	const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const summarizeLastLesson = (last, byId = new Map()) => {
	if (!last || !last.warmups) return '';
	const ex = last.warmups.exercises || [];
	const name = (id) => byId.get(id)?.name || id;
	const parts = ex
		.filter((e) => !e.skipped)
		.map((e) => `${name(e.exerciseId)}${Number.isFinite(e.topReached) ? ` up to ${midiToName(e.topReached)}` : ''}`);
	const when = shortDate(last.date);
	const head = when ? `Last lesson (${when})` : 'Last lesson';
	return `${head}: ${parts.length ? parts.join(' · ') : 'no warmups played'}`;
};

export const _nameToMidiForTests = nameToMidi;
